import axios from "axios";
import { apiClient } from "../../lib/http/apiClient";

export interface NewsGalleryItem {
  key: string;
  url: string;
}

export interface NewsAttachment {
  key: string;
  url: string;
  name: string;
  type: string;
  thumbnail_url?: string;
}

export interface NewsArticle {
  id: string;
  title: string;
  summary?: string;
  body?: string;
  category?: string;
  tags?: string[];
  destinations?: string[];
  source?: string;
  source_author?: string;
  source_url?: string;
  cover_image_url?: string;
  cover_image_key?: string;
  gallery?: NewsGalleryItem[];
  attachments?: NewsAttachment[];
  published_at?: string;
  like_count?: number;
  view_count?: number;
  is_liked?: boolean;
}

export interface NewsListResponse {
  items: NewsArticle[];
  total: number;
  skip: number;
  limit: number;
}

export interface SellerNewsSubmitPayload {
  title?: string;
  summary?: string;
  body?: string;
  category?: string;
  tags?: string[];
  destinations?: string[];
  source?: string;
  source_url?: string;
  source_author?: string;
  cover_image_url?: string;
  cover_image_key?: string;
  gallery_keys?: string[];
  attachment_keys?: string[];
}

interface PresignedUploadResponse {
  upload_url: string;
  key: string;
  expires_in: number;
  content_type: string;
}

interface PresignedRenderResponse {
  url: string;
  key: string;
  expires_in: number;
}

export const newsService = {
  async listNews(skip = 0, limit = 20, category?: string): Promise<NewsListResponse> {
    const { data } = await apiClient.get<NewsListResponse>("/api/news", {
      params: { skip, limit, ...(category ? { category } : {}) },
    });
    return data ?? { items: [], total: 0, skip, limit };
  },

  async submitNews(payload: SellerNewsSubmitPayload): Promise<NewsArticle> {
    const { data } = await apiClient.post<NewsArticle>("/api/news/submit", payload);
    return data;
  },

  async getNewsImagePresignedUpload(filename: string, contentType: string): Promise<PresignedUploadResponse> {
    const { data } = await apiClient.post<PresignedUploadResponse>("/api/storage/presigned-upload", {
      filename,
      content_type: contentType,
      prefix: "sellers/news-images",
    });
    return data;
  },

  async getNewsFilePresignedUpload(filename: string, contentType: string, subfolder: "gallery" | "attachments"): Promise<PresignedUploadResponse> {
    const { data } = await apiClient.post<PresignedUploadResponse>("/api/storage/presigned-upload", {
      filename,
      content_type: contentType,
      prefix: `sellers/news-${subfolder}`,
    });
    return data;
  },

  async uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
    await axios.put(uploadUrl, file, { headers: { "Content-Type": file.type } });
  },

  async uploadImageToS3(uploadUrl: string, file: File): Promise<void> {
    await axios.put(uploadUrl, file, {
      headers: { "Content-Type": file.type },
    });
  },

  async updateMyNews(articleId: string, formData: FormData): Promise<NewsArticle> {
    const { data } = await apiClient.patch<NewsArticle>(`/api/news/${articleId}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },

  async deleteMyNews(articleId: string): Promise<void> {
    await apiClient.delete(`/api/news/${articleId}`);
  },

  async listMyNews(skip = 0, limit = 50): Promise<NewsListResponse> {
    const { data } = await apiClient.get<NewsListResponse>("/api/news/my", {
      params: { skip, limit },
    });
    return data ?? { items: [], total: 0, skip, limit };
  },

  async getNewsImageDisplayUrl(key: string): Promise<string> {
    const { data } = await apiClient.get<PresignedRenderResponse>(
      `/api/storage/presigned-render?key=${encodeURIComponent(key)}`,
    );
    return data.url;
  },
};

